import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {Array<Object>} */
  variants = [];

  /** @type {Object} */
  selectionHistory = {};

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Load variants data
    const variantDataScript = this.querySelector('[data-variant-data]');
    if (variantDataScript) {
      try {
        this.variants = JSON.parse(variantDataScript.textContent);
      } catch (e) {
        console.error('Failed to parse variant data', e);
      }
    }

    this.refreshInternalState();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  refreshInternalState() {
    this.#radios = [];
    this.#checkedIndices = [];
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        // Fallback: finding checked property if attributes invalid
        const realChecked = radios.findIndex(r => r.checked);
        if (realChecked !== -1) {
          this.#checkedIndices.push([realChecked]);
        }
      }
    });

    if (this.variants.length > 0) {
      this.initLinkedOptions();
    }
  }

  initLinkedOptions() {
    // Initial prune based on current server-rendered selection
    this.updateOptionsVisibility();
    // Cache the initial state
    this.updateHistory();
  }

  /**
   * Updates the global history map based on current selections.
   * Format: { 'OptionName:OptionValue': { 'OtherOptionName': 'OtherOptionValue' } }
   */
  updateHistory() {
    const currentSelections = this.selectedOptionsValues; // Array of IDs or values? 
    // The getter returns optionValueId. We need the actual values to match intuitively? 
    // Usually logic depends on position. "Option 1 value" -> "Option 2 value".

    // Let's use indices for robustness if possible, or names.
    // data-input-id="position-index" exists on inputs.

    const fieldsets = Array.from(this.querySelectorAll('fieldset, .variant-option--dropdowns'));

    // Capture current state
    const currentState = {};
    fieldsets.forEach((fieldset, index) => {
      const input = fieldset.querySelector('input:checked, select');
      if (input) {
        const value = input.value; // The value string
        currentState[index] = value;
      }
    });

    // Save preferences
    // For every option, save the configuration of *other* options associated with it.
    Object.keys(currentState).forEach(key => {
      const optionIndex = parseInt(key);
      const optionValue = currentState[key];
      const storageKey = `${optionIndex}:${optionValue}`;

      if (!this.selectionHistory[storageKey]) {
        this.selectionHistory[storageKey] = {};
      }

      // Store the other options' values
      Object.keys(currentState).forEach(otherKey => {
        if (key !== otherKey) {
          this.selectionHistory[storageKey][otherKey] = currentState[otherKey];
        }
      });
    });
  }

  /**
   * Handles the logic when an option changes (Linked Options + State).
   * @param {number} changedOptionIndex 
   * @param {string} newValue 
   */
  handleOptionChangeLogic(changedOptionIndex, newValue) {
    const fieldsets = Array.from(this.querySelectorAll('fieldset, .variant-option--dropdowns'));

    // 1. Identify the desired state based on History
    const storageKey = `${changedOptionIndex}:${newValue}`;
    const history = this.selectionHistory[storageKey] || {};

    // We construct a "proposed" set of selections
    const proposedSelections = [];

    fieldsets.forEach((fieldset, index) => {
      if (index === changedOptionIndex) {
        proposedSelections[index] = newValue;
      } else {
        // Check history, else keep current?
        // The prompt says: "Variant 3 is now selected automatically since it is the only variant... If click Option A again, variant 2 should be selected since user selected it before."

        // Strategy:
        // 1. Try History.
        // 2. Try Current (if valid).
        // 3. Try First Available.

        const historicalValue = history[index];
        if (historicalValue) {
          proposedSelections[index] = historicalValue;
        } else {
          // Fallback to current selection
          const currentInput = fieldset.querySelector('input:checked, select');
          proposedSelections[index] = currentInput ? currentInput.value : null;
        }
      }
    });

    // 2. Validate and adjust the proposed selections from Left to Right (0 -> N)
    // Actually, standard Linked Options is usually 0 -> 1 -> 2.
    // We validate Option 1 based on Option 0. Option 2 based on 0,1.

    const finalSelections = [...proposedSelections];

    for (let i = 0; i < fieldsets.length; i++) {
      if (i === changedOptionIndex) continue; // The user's explicit choice is the anchor.
      // Wait, if 0 changes, 1 and 2 must be valid for 0.
      // If 1 changes, 0 stays (usually), 2 must be valid for 0+1.

      // Let's assume strict hierarchy 0 -> 1 -> 2.
      // If user changed 1, we treat 0 as fixed. 
      // If user changed 2, 0 and 1 are fixed.
      // If user changed 0, everything downstream needs validation.

      if (i > changedOptionIndex) {
        // This is a downstream option. Verify validity.
        const value = finalSelections[i];
        const path = finalSelections.slice(0, i); // Path up to this option

        if (!this.isCombinationAvailable(path, value, i)) {
          // Invalid. Find substitute.
          const firstValid = this.getFirstAvailable(path, i);
          finalSelections[i] = firstValid;
        }
      }
    }

    // 3. Update Visibility (Prune unavailable from DOM)
    // We must do this BEFORE applying selections so that elements are visible (have width) when we measure them.
    this.updateOptionsVisibility(finalSelections);

    // 4. Apply the changes to the DOM inputs
    this.applySelections(finalSelections);

    // 5. Update History with this new valid state
    this.updateHistory();
  }

  isCombinationAvailable(pathValues, targetValue, targetIndex) {
    return this.variants.some(variant => {
      // Check if variant matches path
      const matchPath = pathValues.every((val, idx) => variant.options[idx] === val);
      if (!matchPath) return false;

      // Check if variant has the target value at targetIndex
      return variant.options[targetIndex] === targetValue && variant.available;
    });
  }

  getFirstAvailable(pathValues, targetIndex) {
    const match = this.variants.find(variant => {
      const matchPath = pathValues.every((val, idx) => variant.options[idx] === val);
      return matchPath && variant.available;
    });
    return match ? match.options[targetIndex] : null;
  }

  applySelections(selections) {
    const fieldsets = Array.from(this.querySelectorAll('fieldset, .variant-option--dropdowns'));

    selections.forEach((value, index) => {
      if (!value) return;
      const fieldset = fieldsets[index];

      // Update DOM
      const input = fieldset.querySelector(`input[value="${CSS.escape(value)}"]`) ||
        Array.from(fieldset.querySelectorAll('option')).find(o => o.value === value);

      if (input) {
        if (input.tagName === 'OPTION') {
          const select = input.parentElement;
          select.value = value;
          this.updateSelectedOption(select);
        } else {
          input.checked = true;
          // Always update visual state (pill width/position) because visibility might have changed
          this.updateSelectedOption(input);
        }
      }
    });
  }

  updateOptionsVisibility(currentSelections) {
    if (!currentSelections && !this.selectedOptionsValues) return;

    // If not provided, derive from DOM
    if (!currentSelections) {
      // Warning: selectedOptionsValues returns IDs. We usually work with Values for matching options.
      // Let's grab values directly.
      const fieldsets = Array.from(this.querySelectorAll('fieldset, .variant-option--dropdowns'));
      currentSelections = fieldsets.map(f => {
        const el = f.querySelector('input:checked, select');
        return el ? el.value : null;
      });
    }

    const fieldsets = Array.from(this.querySelectorAll('fieldset, .variant-option--dropdowns'));

    fieldsets.forEach((fieldset, index) => {
      // Option 0 always shows all? 
      // Or if strictly linked, Option 0 shows all.
      // Option 1 shows only those valid with Option 0.
      // Option 2 depends on 0 and 1.

      if (index === 0) return; // Usually root option is always fully visible (or at least all values that exist in ANY variant)

      const path = currentSelections.slice(0, index);

      // Find all possible values for this level given the path
      const validValues = new Set();
      this.variants.forEach(variant => {
        // Must match path
        const matchPath = path.every((val, idx) => variant.options[idx] === val);
        if (matchPath && variant.available) {
          validValues.add(variant.options[index]);
        }
      });

      // Prune DOM (Toggle Visibility & Update Attributes)
      const inputs = fieldset.querySelectorAll('input, option');
      inputs.forEach(input => {
        const val = input.value;
        const container = input.tagName === 'OPTION' ? input : input.closest('.variant-option__button-label');

        if (container) {
          if (validValues.has(val)) {
            // Show & Mark Available
            if (container instanceof HTMLElement) container.style.display = '';
            input.setAttribute('data-option-available', 'true');
            input.removeAttribute('aria-disabled');
          } else {
            // Hide & Mark Unavailable
            if (container instanceof HTMLElement) container.style.display = 'none';
            input.setAttribute('data-option-available', 'false');
            input.setAttribute('aria-disabled', 'true');
          }
        }
      });
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    // INTERCEPT: Run Linked Options Logic
    if (this.variants.length > 0) {
      // Determine which index changed
      let changedIndex = -1;
      const fieldsets = Array.from(this.querySelectorAll('fieldset, .variant-option--dropdowns'));

      if (event.target.tagName === 'SELECT') {
        changedIndex = fieldsets.indexOf(event.target.closest('.variant-option'));
      } else {
        changedIndex = parseInt(event.target.dataset.fieldsetIndex);
      }

      if (changedIndex !== -1) {
        this.handleOptionChangeLogic(changedIndex, event.target.value);
      }
    }

    // Proceed with standard update (Update visual state of the clicked element, dispatch, fetch)
    // Note: handleOptionChangeLogic might have changed *other* inputs. 
    // We still want the standard behavior to run to trigger the fetch for Price updates etc.

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    // Update primary image per the selected variant
    const gallery = this.closest('main')?.querySelector('.product-media-wrapper');
    console.log("gallery", gallery);
    if (gallery && gallery.dataset.updateGalleryOnVariantChange === 'true') {
      this.updateGallery();
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
            radios[currentIndex].parentElement?.removeAttribute('data-selected');
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
            radios[previousIndex].parentElement?.removeAttribute('data-selected');
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            radios[newCurrentIndex].parentElement?.setAttribute('data-selected', 'true');
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            radios[newPreviousIndex].parentElement?.removeAttribute('data-selected');
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
      target.parentElement?.setAttribute('data-selected', 'true');
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]:not([data-variant-data])`)?.textContent;
        // Optimization: We could update this.variants here if needed, but variants usually don't change unless new product logic.

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          // Check for product change (Combined Listing)
          const newVariantPickerSource = html.querySelector(this.tagName.toLowerCase());
          const newProductId = newVariantPickerSource?.dataset.productId;
          const isNewProduct = newProductId && this.dataset.productId !== newProductId;

          let newProduct;
          if (isNewProduct) {
            // If it's a different product, we MUST morph to load new options/variants
            newProduct = this.updateVariantPicker(html);
            this.refreshInternalState();
            // Re-apply visibility pruning after morph
            if (this.variants.length > 0) {
              this.updateOptionsVisibility();
            }
          } else {
            // If same product, SKIP morphing "this".
            // We rely on client-side logic to have already updated visual state.
            // We ONLY update specific data attrs if needed, but usually not.
          }

          if (this.selectedOptionId) {
            // Need to handle potential null textContent if server response is partial
            const textContent = html.querySelector(`variant-picker script[type="application/json"]:not([data-variant-data])`)?.textContent;
            const variantData = textContent ? JSON.parse(textContent) : null;

            if (variantData) {
              this.dispatchEvent(
                new VariantUpdateEvent(variantData, this.selectedOptionId, {
                  html,
                  productId: this.dataset.productId ?? '',
                  newProduct,
                })
              );
            }
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Logs the selected variant data to the console.
   */
  updateGallery() {
    if (!this.variants || this.variants.length === 0) {
      console.log('No variant data available');
      return;
    }

    // Get current selected options
    const fieldsets = Array.from(this.querySelectorAll('fieldset, .variant-option--dropdowns'));
    const selectedOptionValues = fieldsets.map(f => {
      const el = f.querySelector('input:checked, select');
      return el ? el.value : null;
    });

    // Find the matching variant
    const selectedVariant = this.variants.find(variant => {
      return variant.options.every((option, index) => option === selectedOptionValues[index]);
    });

    if (selectedVariant) {
      const firstImage = document.querySelector('.media-gallery__grid-item--0 img');
      const widths = [240, 352, 832, 1200, 1600, 1920, 2560, 3840];
      const srcSet = widths.reduce((acc, width) => {
        const url = `${selectedVariant.featured_image.src}?width=${width}`;
        return `${acc}${url} ${width}w, `;
      });
      console.log('first image element:', firstImage);
      firstImage?.setAttribute('src', selectedVariant.featured_image.src);
      firstImage?.setAttribute('srcset', srcSet);
      console.log('first image element:', firstImage);
    } else {
      console.log('No matching variant found for selection:', selectedOptionValues);
    }
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      // It's possible for valid options not to have ID if they are just values? 
      // But typically Shopify modern themes use IDs. 
      // If error, return null/undefined to be safe?
      // throw new Error('No option value ID found');
      return undefined;
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) return ''; // fallback

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
